import { useEffect, useState } from 'react';
import { IconArrowLeft, IconBell, IconChevronLeft, IconChevronRight, IconDownload, IconLayoutSidebarRight, IconMoon } from '@tabler/icons-react';
import { HeaderTool, Page, PageHeader } from '@hana/shell';
import { useT } from '@hana/i18n';
import { Button, Skeleton, StateView, useTheme, useToast } from '@hana/ui';
import { readFinanceAccess } from '../access';
import { CAR, CCF, CUS, DDL, DN, DOC, IB, JDN, JOB, LS, MS, OB, PC, PI, PT, RF, SCY, SVCJ, TR, TRF, ODN, avg, delta, fi, pc, periodOf, sum, type Mode } from './data';
import { BodDock } from './BodDock';
import { BodPhone } from './BodPhone';
import { SvcTab } from './SvcTab';
import { FleetTab, SlaTab } from './OpsTabs';
import { EvtTab } from './EvtTab';
import { FinTab, LossTab, TaxTab } from './MoneyTabs';
import { HrTab, OvTab } from './OvHr';
import s from './bod.module.css';
import './i18n';

// Mint-BOD (1536) tab Tổng quan · Nhân sự · Hoạt động KD: chỉ đếm, không có số tiền. Các tab khác + dock/drawer: sau mốc desktop (N-03).
export type TabKey = 'ov' | 'fin' | 'tax' | 'loss' | 'hr' | 'ops' | 'svc' | 'sla' | 'fleet' | 'evt';
const MODES: readonly Mode[] = ['m', 'q', 'y'];
const SEG = [['Standby', 215, 'cWarn'], ['Active', 320, 'cPri'], ['Released', 1251, 'cOk'], ['Cancelled', 5, 'cNone']] as const;
const ST = [['BK Standby', 180, 'cWarn'], ['RQ Standby', 30, 'cWarn'], ['RQ Request', 5, 'cWarn'], ['BK Confirmed', 124, 'cPri'], ['BL Hold', 159, 'cPri'], ['BL Wait RLS', 37, 'cPri'], ['BL Released', 1251, 'cOk', 'bod.notFinish'], ['No Show', 5, 'cNone']] as const;
const CHART = [['OB', OB, 'fOb'], ['LS', LS, 'fLs'], ['TR', TR, 'fTr'], ['IB', IB, 'fIb']] as const;
const CW = 720, CH = 180;

const spark = (a: readonly number[]): string => {
  const mn = Math.min(...a), r = Math.max(...a) - mn || 1;
  return a.map((v, i) => `${i ? 'L' : 'M'}${(2 + (i * 116) / (a.length - 1)).toFixed(1)} ${(21 - ((v - mn) / r) * 18).toFixed(1)}`).join(' ');
};

function Tile({ k, v, sub, good, sp, flat }: { k: string; v: string; sub: string; good?: boolean | null; sp?: readonly number[]; flat?: boolean }) {
  const t = useT();
  return (
    <div className={`${s.tile} ${flat ? s.flat : ''}`}>
      <span className={s.tk}>{k}</span>
      <b className={s.tv}>{v}</b>
      <span className={`${s.ts} ${good === true ? s.up : good === false ? s.down : ''}`}>{sub}</span>
      {sp && <svg role="img" aria-label={t('bod.spark', { k, a: MS[0]!, b: MS[11]! })} width="100%" height="24" viewBox="0 0 120 24" preserveAspectRatio="none"><path d={`${spark(sp)} L118 24 L2 24 Z`} className={s.spA} /><path d={spark(sp)} className={s.spL} vectorEffect="non-scaling-stroke" /></svg>}
    </div>
  );
}

export function Bod({ tab = 'ov' }: { tab?: TabKey }) {
  const t = useT();
  const toast = useToast();
  const { toggle: toggleTheme } = useTheme();
  const access = readFinanceAccess(typeof location === 'undefined' ? '' : location.search);
  const MONEY: readonly TabKey[] = ['fin', 'tax', 'loss'];
  const state = access.force ?? (MONEY.includes(tab) && !access.money ? 'noperm' : 'ready');
  const [mode, setMode] = useState<Mode>('m');
  const [mi, setMi] = useState(11);
  const [qi, setQi] = useState(4);
  const [dense, setDense] = useState(false);
  const [wide, setWide] = useState(() => matchMedia('(min-width: 1800px)').matches);
  const [right, setRight] = useState(true);
  const [phone, setPhone] = useState(() => matchMedia('(max-width: 719px)').matches);
  useEffect(() => {
    const mq = matchMedia('(min-width: 1800px)');
    const mp = matchMedia('(max-width: 719px)');
    const on = () => { setWide(mq.matches); setPhone(mp.matches); };
    mq.addEventListener('change', on);
    mp.addEventListener('change', on);
    return () => { mq.removeEventListener('change', on); mp.removeEventListener('change', on); };
  }, []);
  const later = () => toast({ msg: t('bod.later') });
  const p = periodOf(mode, mi, qi);
  const step = (d: number) => (mode === 'm' ? setMi(mi + d) : setQi(qi + d));
  const dTile = (k: string, a: readonly number[]) => { const c = sum(a, p.idx), pr = sum(a, p.pidx), d = delta(c, pr); return <Tile key={k} k={k} v={fi(c)} sub={dText(d[0])} good={d[1]} sp={a} />; };
  const rfP = sum(JOB, p.idx) ? (sum(RF, p.idx) / sum(JOB, p.idx)) * 100 : 0;
  const avgK = mode === 'm' ? '' : t('bod.avgSuffix');
  const dText = (v: string): string => (v === 'kỳ trước: —' ? t('bod.prevNone') : v.replace(' so kỳ trước', t('bod.vsPrev')));
  const mx = 560;
  const stack = CHART.map(([, a]) => a);
  const base = new Array<number>(12).fill(0);
  const bars = stack.map((a) => a.map((v, i) => { const h = (Math.max(0, v) / mx) * (CH - 12), y = CH - base[i]! - h; base[i]! += h; return h > 0.2 ? `M${(i * 60 + 13.2).toFixed(1)} ${y.toFixed(1)}h33.6v${h.toFixed(1)}h-33.6z ` : ''; }).join(''));
  const SUB: readonly (readonly [string, readonly number[], string, string])[] = [[t('bod.s.trf'), TRF, '145', 'TblTRFServiceData'], [t('bod.s.doc'), DOC, '206', 'TblDOCServiceData'], [t('bod.s.ccf'), CCF, '175', 'TblCCFServiceData'], [t('bod.s.scy'), SCY, '47', 'TblSCYServiceData'], [t('bod.s.ddl'), DDL, '79', 'TblDDLServiceData'], [t('bod.s.svcj'), SVCJ, '—', 'distinct JobID']];
  const sub = SUB.map(([k, a, op, src]) => { const c = sum(a, p.idx), pr = sum(a, p.pidx), d = delta(c, pr); return { k, c, pr, op, src, d }; });
  const subMax = Math.max(1, ...sub.map((r) => Math.max(r.c, r.pr)));
  const stMax = Math.max(...ST.map((x) => x[1]));

  return (
    <Page kind="table" dock={wide && right && state !== 'noperm' ? <BodDock onClose={() => setRight(false)} onNote={(m) => toast({ msg: m })} /> : undefined}>
      <PageHeader
        title={t('bod.title')}
        findHint={false}
        dense
        lead={<button type="button" className={s.back} aria-label={t(phone ? 'bod.ph.back' : 'bod.backLabel')} title={t(phone ? 'bod.ph.back' : 'bod.backLabel')} onClick={later}><IconArrowLeft size={18} aria-hidden="true" /></button>}
        meta={<span className={s.per}>{p.label}</span>}
        tabs={
          <div className={s.ctls}>
            <div className={s.seg} role="radiogroup" aria-label={t('bod.modeLabel')}>
              {MODES.map((k) => <button key={k} type="button" role="radio" aria-checked={mode === k} onClick={() => setMode(k)}>{t(`bod.mode.${k}`)}</button>)}
            </div>
            {!phone && <div className={s.seg}>
              <button type="button" className={s.arrow} aria-label={t('bod.prev')} title={t('bod.prev')} disabled={!p.canPrev} onClick={() => step(-1)}><IconChevronLeft size={16} aria-hidden="true" /></button>
              <span className={s.pshort}>{p.short}</span>
              <button type="button" className={s.arrow} aria-label={t('bod.next')} title={t('bod.next')} disabled={!p.canNext} onClick={() => step(1)}><IconChevronRight size={16} aria-hidden="true" /></button>
            </div>}
            {!phone && <div className={s.seg} role="group" aria-label={t('bod.density')}>
              {([['bod.dense.std', false], ['bod.dense.cmp', true]] as const).map(([l, d]) => <button key={l} type="button" aria-pressed={dense === d} onClick={() => setDense(d)}>{t(l)}</button>)}
            </div>}
          </div>
        }
        actions={phone ? <HeaderTool label={t('bod.ph.dark')} icon={IconMoon} onClick={toggleTheme} /> : (
          <>
            <HeaderTool label={t('bod.notif')} icon={IconBell} onClick={later} />
            <HeaderTool label={t('bod.openRight')} icon={IconLayoutSidebarRight} active={right} onClick={() => setRight((v) => !v)} />
            <Button variant="primary" icon={<IconDownload size={16} aria-hidden="true" />} onClick={later}>{t('bod.export')}</Button>
          </>
        )}
      />
      <div className={s.page}>
        {phone ? <BodPhone state={state} mode={mode} onMode={setMode} idx={p.idx} pidx={p.pidx} onKpi={later} onPick={later} /> : <>
        <StateView state={state} skeleton={<Skeleton rows={9} />}>
          {tab === 'fin' ? <FinTab idx={p.idx} pidx={p.pidx} onMonth={(i) => { setMode('m'); setMi(i); }} /> : tab === 'tax' ? <TaxTab idx={p.idx} pidx={p.pidx} /> : tab === 'loss' ? <LossTab idx={p.idx} /> : tab === 'ov' ? <OvTab idx={p.idx} onLater={later} notify={(m) => toast({ msg: m })} /> : tab === 'hr' ? <HrTab idx={p.idx} onLater={later} notify={(m) => toast({ msg: m })} /> : tab === 'svc' ? <SvcTab idx={p.idx} pidx={p.pidx} /> : tab === 'sla' ? <SlaTab idx={p.idx} /> : tab === 'fleet' ? <FleetTab idx={p.idx} /> : tab === 'evt' ? <EvtTab /> : <div className={s.grid}>
            <section aria-label={t('bod.sec.metrics')} className={`${s.span12} ${s.tiles} ${s.t8}`}>
              {dTile(t('bod.t.jobs'), JOB)}{dTile(t('bod.t.ob'), OB)}{dTile(t('bod.t.ls'), LS)}{dTile(t('bod.t.tr'), TR)}
              <Tile k={t('bod.t.rf')} v={fi(sum(RF, p.idx))} sub={t('bod.t.rfSub', { p: pc(rfP) })} />
              <Tile k={`${t('bod.t.cus')}${avgK}`} v={fi(avg(CUS, p.idx))} sub={t('bod.t.cusSub')} />
              <Tile k={`${t('bod.t.car')}${avgK}`} v={fi(avg(CAR, p.idx))} sub={t('bod.t.carSub')} />
              {dTile(t('bod.t.trf'), TRF)}
            </section>
            <section aria-label={t('bod.chart')} className={`${s.card} ${s.span8}`}>
              <div className={s.ph}><h2>{t('bod.chart')}</h2><span>{t('bod.chartSub')}</span></div>
              <div className={s.leg}>{CHART.map(([k, , c]) => <span key={k}><i className={s[c]} />{k}</span>)}<em>{t('bod.peak')}</em></div>
              <svg role="img" aria-label={t('bod.chartAria', { v: MS.map((m, i) => `${m} ${fi(JOB[i]!)}`).join(', ') })} width="100%" height="180" viewBox={`0 0 ${CW} ${CH}`} preserveAspectRatio="none">
                <path d={p.idx.map((i) => `M${i * 60 + 2} 0h56v${CH}h-56z`).join(' ')} className={s.hl} />
                <path d={[0.25, 0.5, 0.75, 1].map((f) => `M0 ${(CH - f * (CH - 12)).toFixed(1)}H${CW}`).join(' ') + ` M0 ${CH - 0.5}H${CW}`} className={s.gridL} vectorEffect="non-scaling-stroke" />
                {bars.map((d, i) => <path key={i} d={d} className={s[CHART[i]![2]]} />)}
              </svg>
              <div className={s.xl}>{MS.map((k, i) => <span key={k} className={p.idx.includes(i) ? s.xon : ''}>{k}</span>)}</div>
            </section>
            <section aria-label={t('bod.open')} className={`${s.card} ${s.span4}`}>
              <div className={s.ph}><h2>{t('bod.open')}</h2><span>{t('bod.openSub')}</span></div>
              <div role="img" aria-label={t('bod.openAria', { v: SEG.map((x) => `${x[0]} ${x[1]}`).join(', ') })} className={s.segBar}>{SEG.map(([k, n, c]) => <div key={k} className={s[c]} style={{ flex: `${n} 1 0` }} />)}</div>
              <div className={s.segLeg}>{SEG.map(([k, n, c]) => <span key={k}><i className={s[c]} />{k} <b>{fi(n)}</b></span>)}</div>
              <div role="img" aria-label={t('bod.openAria2')} className={s.bars1}>
                {ST.map((r, i) => (
                  <div key={r[0]} className={s.br} style={{ borderTop: i ? undefined : '0' }}>
                    <div className={s.bl}><i className={s[r[2]]} /><span>{r[0]}</span>{r[3] && <small>{t(r[3])}</small>}</div>
                    <div className={s.bt}><div className={s[r[2]]} style={{ width: `${Math.max(2, (r[1] / stMax) * 100).toFixed(1)}%` }} /></div>
                    <b className={s.bv}>{fi(r[1])}</b>
                  </div>
                ))}
              </div>
              <div className={s.list}>
                {[['bod.etd14', '248'], ['bod.cus90', '108']].map(([k, v], i) => <div key={k} style={{ borderTop: i ? undefined : '0' }}><span>{t(k!)}</span><b>{v}</b></div>)}
              </div>
            </section>
            <section aria-label={t('bod.sub')} className={`${s.card} ${s.span12}`}>
              <div className={s.ph}><h2>{t('bod.sub')}</h2><span>{t('bod.subSub')}</span></div>
              <div className={s.leg}><span><i className={s.cPri} />{t('bod.thisP')}</span><span><i className={s.mk} />{t('bod.prevP')}</span></div>
              <div role="img" aria-label={t('bod.subAria', { v: sub.map((r) => `${r.k} ${fi(r.c)}`).join(', ') })} className={s.bars2}>
                {sub.map((r, i) => (
                  <div key={r.k} className={s.br} style={{ borderTop: i < 2 ? '0' : undefined }}>
                    <div className={s.bl2}><span>{r.k}</span><small>{t('bod.opening', { src: r.src, n: r.op })}</small></div>
                    <div className={s.bt}><div className={s.cPri} style={{ width: `${Math.max(2, (r.c / subMax) * 100).toFixed(1)}%` }} />{r.pr > 0 && <div className={s.pmk} style={{ left: `${((r.pr / subMax) * 100).toFixed(1)}%` }} />}</div>
                    <b className={s.bv2}>{fi(r.c)}<span className={r.d[1] === true ? s.up : r.d[1] === false ? s.down : ''}>{r.pr ? t('bod.prevVal', { d: r.d[0].replace(' so kỳ trước', ''), v: fi(r.pr) }) : t('bod.prevNone2')}</span></b>
                  </div>
                ))}
              </div>
            </section>
            <section aria-label={t('bod.fin')} className={`${s.card} ${s.span12}`}>
              <div className={s.ph}><h2>{t('bod.fin')}</h2><span>{t('bod.finSub')}</span></div>
              <div className={`${s.tiles} ${s.t6}`}>
                {dTile(t('bod.f.dn'), DN)}{dTile(t('bod.f.jdn'), JDN)}{dTile(t('bod.f.pt'), PT)}{dTile(t('bod.f.pi'), PI)}{dTile(t('bod.f.pc'), PC)}{dTile(t('bod.f.odn'), ODN)}
              </div>
            </section>
          </div>}
        </StateView>
        </>}
      </div>
    </Page>
  );
}
